/**
 * A channel tour: one data set shown in turn as the channel's views show it,
 * a table, a board, a calendar and a dashboard, switched through the view
 * picker as a person would, then the channel's Overview. Plain data and a
 * timeline, like a replay's, so a server-rendered first frame can hand the
 * tour to its player.
 */

/** A view of the data set the tour shows. */
export type TourView = "table" | "board" | "calendar" | "dashboard" | "overview";

/** How long the tour holds each view before switching to the next. */
const HOLD_MS: Readonly<Record<TourView, number>> = {
  table: 4_400,
  board: 4_400,
  calendar: 5_600,
  dashboard: 9_600,
  overview: 6_000,
};

/**
 * A view taller than the frame, read top first, then scrolled down: the ms
 * into its hold at which it scrolls down, and back up. The calendar's weeks
 * with records stay in view until the tour moves on; the dashboard is back
 * at its top before it does.
 */
const SCROLL_MS: Readonly<Partial<Record<TourView, readonly [down: number, up: number]>>> = {
  calendar: [2_000, HOLD_MS.calendar],
  dashboard: [3_200, 7_600],
};

/** When each view appears, in tour ms, and when the tour ends. */
export type TourTimeline = {
  steps: readonly (readonly [atMs: number, view: TourView])[];
  endsMs: number;
};

export function tourTimeline(views: readonly TourView[]): TourTimeline {
  if (views.length === 0) throw new Error("A channel tour needs at least one view.");
  let atMs = 0;
  const steps = views.map((view) => {
    const step = [atMs, view] as const;
    atMs += HOLD_MS[view];
    return step;
  });
  return { steps, endsMs: atMs };
}

function stepAt(timeline: TourTimeline, atMs: number): readonly [atMs: number, view: TourView] {
  return [...timeline.steps].reverse().find(([stepMs]) => stepMs <= atMs) ?? timeline.steps[0]!;
}

/** The view on screen `atMs` into the tour. */
export function tourViewAt(timeline: TourTimeline, atMs: number): TourView {
  return stepAt(timeline, atMs)[1];
}

/** Whether the view on screen `atMs` into the tour is scrolled down through its lower part. */
export function tourScrolledAt(timeline: TourTimeline, atMs: number): boolean {
  const [stepMs, view] = stepAt(timeline, atMs);
  const scroll = SCROLL_MS[view];
  return scroll !== undefined && atMs - stepMs >= scroll[0] && atMs - stepMs < scroll[1];
}
